import { FormattedMessage } from "react-intl";
import classNames from "classnames";
import { useSelector } from "redux/hooks";
import { DeleteIcon } from "pages/Workspaces/components/icons/TreadsIcons";
import { useState, useEffect, useCallback, useRef } from "react";
import { deleteSingleChatHistory, removeMultipleChat, IChat, getAllChats, setSearchWord, setWsSearchQuery, deleteAllChats } from "redux/actions";
import useRouter from "hooks/useRouter";
import { Spinner } from "components";
import { useAppNotification } from "hooks/services/AppNotification";
import { RoutePaths } from "pages/routePaths";
import { getRelativeTime, formatFullDate } from "utils/constants";
import Pagination from "./Pagination";
import styles from "../threads.module.scss";
import { ChatRoute } from "pages/ChatPage";
import { SearchField } from "views/layout/Sidebar/component/ChatSidebar/components/SearchField";
import { setfolderchats } from "redux/actions/chatFolder";
import { removeQuotes } from "utils/functions";

interface HistoryListProps {
    onSearchFocus?: () => void;
    onSearchBlur?: () => void;
}
export const HistoryList = ({ onSearchFocus, onSearchBlur }: HistoryListProps) => {

    const { theme } = useSelector((state) => state.authReducer);
    const [hoveredThreadId, setHoveredThreadId] = useState<number | null>(null);
    const { perPageVal, chathistory, PrivateChat } = useSelector((state) => state.chatReducer);

    const { Folders } = useSelector((state) => state.chatFolderReducer);
    const { pathname, push } = useRouter();
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isLoadMoreChats, setIsLoadMoreChats] = useState<boolean>(false);

    const { triggerNotification } = useAppNotification();
    const [loadingThreads, setLoadingThreads] = useState<{ [key: number]: boolean }>({});
    const { searchWsQuery, loadFolder, showCreateWorkspace } = useSelector((state) => state.workSpaceReducer);
    // Get last folder and its chats
    const lastFolder = Folders && Folders.length > 0 ? Folders[Folders.length - 1] : null;
    const lastFolderChats = lastFolder ? lastFolder.chats : [];
    const inputRef = useRef<HTMLInputElement | null>(null);
    const lastPage = lastFolder?.pagination?.lastPage || 1;

    const chatsForCurrentPage = lastFolderChats.filter(chat => chat.page === currentPage);
    const searchQueryRef = useRef(searchWsQuery);
    const isFirstRender = useRef(true);
    const searchContainerRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        let isMounted = true;
        if (searchWsQuery) {
            setIsLoading(true);
            const delayDebounce = setTimeout(() => {
                setIsLoading(true);
                deleteAllChats();
                getAllChats({ search: searchWsQuery })
                    .then(() => {
                        if (isMounted)
                            setIsLoading(false);
                    })
                    .catch((err: any) => {
                        if (isMounted)
                            setIsLoading(false);
                        triggerNotification({ message: err?.data?.message, type: "error" });
                    });
            }, 1500);
            return () => {
                isMounted = false;
                clearTimeout(delayDebounce);
                if (isMounted) setIsLoading(false);
            }
        }
    }, [searchWsQuery]);

    const getChats = useCallback(
        () => {
            setIsLoadMoreChats(true);
            getAllChats({ search: "", page: currentPage, perPage: (perPageVal && perPageVal > 9) ? perPageVal : 10, folder_id: 0 })
                .finally(() => {
                    setIsLoadMoreChats(false);
                });
        },
        [currentPage]
    );

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (chatsForCurrentPage.length === 0) {
            getChats()
        }
    }, [currentPage]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            // If click is outside the search container
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
                // Only call onSearchBlur if searchWsQuery is empty
                if (onSearchBlur && !searchWsQuery) {
                    onSearchBlur();
                }
            }
        }

        // Add event listener when component mounts
        document.addEventListener("mousedown", handleClickOutside);

        // Clean up event listener when component unmounts
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [onSearchBlur, searchWsQuery]);


    const handlePageChange = (selectedItem: { selected: number }) => {
        setCurrentPage(selectedItem.selected + 1);
    };

    const handleDeleteThread = async (id: number) => {
        try {
            setLoadingThreads((prev) => ({ ...prev, [id]: true }));

            const pathSegments = pathname.split('/');
            const chatIdFromUrl = pathSegments[pathSegments.length - 2];

            if (id === Number(chatIdFromUrl)) {
                push(`/${RoutePaths.Chat}`);
            }

            const folder_id = 0;

            deleteSingleChatHistory(id, folder_id);
            if (chatsForCurrentPage.length <= 1) {
                if (currentPage < lastPage) {
                    const page2Chats = lastFolderChats.filter(chat => chat.page === currentPage + 1);
                    if (page2Chats.length >= 1) {
                        const updatedPage2Chats = page2Chats.map(chat => ({
                            ...chat,
                            page: currentPage,
                        }));
                        setfolderchats(
                            updatedPage2Chats,
                            { page: currentPage, lastPage: lastPage, total: lastFolder?.pagination?.total, },
                            folder_id
                        );
                    }
                    else {
                        getChats();
                    }
                    
                }
                if(currentPage === lastPage){
                    const page1Chats = lastFolderChats.filter(chat => chat.page === currentPage - 1);
                    if (page1Chats.length >= 1) {
                        // Navigate to previous page
                        if (currentPage > 1) {
                            handlePageChange({ selected: currentPage - 2 });
                        }
                    } 
                }
            }

            removeMultipleChat([id])
                .then((resp: any) => {
                    if (resp?.message === "Chats deleted successfully") {
                        triggerNotification({
                            message: "Chat deleted successfully",
                            type: "info",
                        });
                    }
                    setLoadingThreads((prev) => ({ ...prev, [id]: false }));
                })
                .catch((err) => {
                    setLoadingThreads((prev) => ({ ...prev, [id]: false }));
                    triggerNotification({ message: err?.data?.message, type: "error" });
                });

        } catch (error) {
            setLoadingThreads((prev) => ({ ...prev, [id]: false }));
            triggerNotification({ message: error?.data?.message, type: "error" });
        }
    };
    useEffect(() => {
        searchQueryRef.current = searchWsQuery;
    }, [searchWsQuery]);

    const onClearSearchField = () => {
        setIsLoading(false);
        setWsSearchQuery && setWsSearchQuery("");
        setSearchWord("");
        if (inputRef.current) inputRef.current.focus();
        if (onSearchBlur) onSearchBlur();
    };

    const handleSerchFocus = () => {
        if (inputRef.current) inputRef.current.focus();
        if (onSearchFocus) onSearchFocus();
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setWsSearchQuery && setWsSearchQuery(value);
        setSearchWord("");
        if (!value) {
            setIsLoading(false);
        }
        if (onSearchFocus) {
            onSearchFocus(); // Keep the search field active while user is typing
        }
    };

    const showChatsInfo = !isLoading &&
        (searchWsQuery ? chathistory?.length > 0 : (lastFolder?.pagination?.total ?? 0) > 0);
    return (
        <div className={`font-inter ${styles.mainContainer} ${styles.history} ${(PrivateChat || showCreateWorkspace) ? styles.isbackdrop : ""}`}>
            <div className={styles.historyListContainer} ref={searchContainerRef}
            >
                {showChatsInfo && (
                    <div className={`${styles.threadsHeading} ${styles.historyList}`}>
                        <div className={classNames(styles.title, {
                            [styles.light]: theme === "light",
                            [styles.dark]: theme === "dark",
                        })}>
                            <FormattedMessage id="workspace.history" />
                        </div>

                        <h5 className={classNames(styles.countThreads, {
                            [styles.light]: theme === "light",
                            [styles.dark]: theme === "dark",
                        })}>
                            
                            ({searchWsQuery ?
                                (chathistory?.length === 1 ?
                                    <>1 <FormattedMessage id="workspace.chat" /></> :
                                    <>{chathistory?.length} <FormattedMessage id="workspace.chats" /></>
                                ) :
                                (lastFolder?.pagination?.total === 1 ?
                                    <>1 <FormattedMessage id="workspace.chat" /></> :
                                    <>{lastFolder?.pagination?.total ?? 0} <FormattedMessage id="workspace.chats" /></>
                                )})
                        </h5>

                    </div>
                )}
                <div className={styles.searchFieldWrapper}>
                    {(searchWsQuery || (lastFolder?.pagination?.total ?? 0) > 0) && (
                        <SearchField
                            inputRef={inputRef}
                            value={searchWsQuery}
                            onChange={(e) => {
                                handleSearchChange(e);
                            }}
                            onClear={onClearSearchField}
                            chatsidebar={true}
                            handleSerchFocus={handleSerchFocus}
                            onFocus={handleSerchFocus}
                            onBlur={() => {
                                if (onSearchBlur && searchWsQuery === "") {
                                    onSearchBlur();
                                }
                            }}
                        />
                    )}
                </div>
            </div>
            {(isLoading || loadFolder || isLoadMoreChats) ? (
                <div className="flex justify-center items-center">
                    <Spinner small />
                </div>
            ) : (
                <>
                    {searchWsQuery ? (
                        chathistory &&
                        chathistory.length > 0 &&
                        chathistory.map((chat: IChat) => (
                            <div
                                key={chat.id}
                                className={classNames(styles.cardContainer, {
                                    [styles.light]: theme === "light",
                                    [styles.dark]: theme === "dark",
                                    [styles.hover]: hoveredThreadId === chat.id,
                                })}
                                onMouseEnter={() => setHoveredThreadId(chat.id)}
                                onMouseLeave={() => setHoveredThreadId(null)}
                                onClick={() => push(`/${RoutePaths.Chat}/${ChatRoute.History}/${chat?.id}/${0}`)}
                            >
                                <div className={styles.headWrapper}>
                                    <div className={`${styles.nameContainer} flex-1 min-w-0 pr-4`}>
                                        <div className={classNames(styles.cardTitle, "truncate font-inter", {
                                            [styles.light]: theme === "light",
                                            [styles.dark]: theme === "dark",
                                        })}>
                                            {removeQuotes(chat.topic) || "New chat"}
                                        </div>
                                    </div>
                                    <div className={styles.threadActions}>
                                        {hoveredThreadId === chat.id && (
                                            <>
                                                {loadingThreads[chat.id] ? (
                                                    <Spinner extraSmall />
                                                ) : (
                                                    <div className={styles.delIcon} onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteThread(chat.id);
                                                    }}>
                                                        <DeleteIcon />
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        <div className={classNames("font-inter", styles.date, {
                                            [styles.light]: theme === "light",
                                            [styles.dark]: theme === "dark",
                                        })}>
                                            {hoveredThreadId === chat.id
                                                ? formatFullDate(chat.updated_at || "")
                                                : getRelativeTime(chat.updated_at || "")}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="flex flex-col gap-[10px]">
                            {chatsForCurrentPage.map((chat: IChat) => (
                                <div
                                    key={chat.id}
                                    className={classNames(styles.cardContainer, {
                                        [styles.light]: theme === "light",
                                        [styles.dark]: theme === "dark",
                                        [styles.hover]: hoveredThreadId === chat.id,
                                    })}
                                    onMouseEnter={() => setHoveredThreadId(chat.id)}
                                    onMouseLeave={() => setHoveredThreadId(null)}
                                    onClick={() => push(`/${RoutePaths.Chat}/${ChatRoute.History}/${chat?.id}/${0}`)}
                                >
                                    <div className={styles.headWrapper}>
                                        <div className={`${styles.nameContainer} flex-1 min-w-0 pr-4`}>
                                            <div className={classNames(styles.cardTitle, "truncate font-inter", {
                                                [styles.light]: theme === "light",
                                                [styles.dark]: theme === "dark",
                                            })}>
                                                {removeQuotes(chat.topic) || "New chat" }
                                            </div>
                                        </div>
                                        <div className={styles.threadActions}>
                                            {hoveredThreadId === chat.id && (
                                                <>
                                                    {loadingThreads[chat.id] ? (
                                                        <Spinner extraSmall />
                                                    ) : (
                                                        <div className={styles.delIcon} onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteThread(chat.id);
                                                        }}>
                                                            <DeleteIcon />
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                            <div className={classNames("font-inter", styles.date, {
                                                [styles.light]: theme === "light",
                                                [styles.dark]: theme === "dark",
                                            })}>
                                                {hoveredThreadId === chat.id
                                                    ? formatFullDate(chat.updated_at || "")
                                                    : getRelativeTime(chat.updated_at || "")}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div>
                        {(chatsForCurrentPage.length > 0 && lastPage > 1 && !searchWsQuery) &&
                            <div className="justify-center flex font-normal font-inter mb-[40px]">
                                <Pagination pageCount={lastPage}
                                    onPageChange={handlePageChange}
                                    forcePage={currentPage - 1}
                                />
                            </div>
                        }
                    </div>
                </>
            )}
        </div>
    );
}